/**
 * @file useShopSiteUrl.ts
 * @description Адрес сайта магазина для ссылок «открыть на сайте» вне
 * конструктора (phase19.16, партия «Юридические документы»;
 * uiux-legal-steps-b-c §2: «Принятые условия» в заказе и редакции в
 * «Согласиях» клиента ведут на страницу редакции на сайте).
 *
 * Источник тот же, что у конструктора (`useSiteView().siteUrl`):
 * `GET /admin/site/preview-config` → `webBaseUrl` (домены магазинов —
 * phase19.28). Ответ запоминается для кабинета вкладки (смена магазина
 * сбрасывает его в AuthProvider): адрес сайта не меняется
 * за время работы, а карточек заказа менеджер открывает много. Нет права или
 * сайт не настроен — `null`: ссылки показываются текстом без перехода.
 *
 * Сервер отдаёт в кабинет абсолютные адреса редакций (`legalAcceptances[].href`,
 * `legalRevision.href`) — они берутся как есть; адрес сайта нужен только как
 * запас для относительных (`needed` — есть ли такие).
 */

import { useEffect, useState } from "react";

import { sitePages } from "../lib/api-site-pages";
import { getTabContextId } from "../lib/tab-context";

/** Запомненный адрес — для кабинета вкладки `key` (у другого магазина свой сайт). */
let cached: { key: string; promise: Promise<string | null> } | null = null;

/** Адрес сайта (без `/` в конце) или null. */
function loadSiteUrl(): Promise<string | null> {
  const key = getTabContextId() ?? "";
  if (cached?.key === key) return cached.promise;
  const entry: { key: string; promise: Promise<string | null> } = { key, promise: Promise.resolve(null) };
  entry.promise = sitePages
    .previewConfig()
    .then((cfg) => cfg.webBaseUrl?.replace(/\/+$/, "") || null)
    .catch(() => {
      // Ошибку не запоминаем: следующая карточка спросит снова.
      if (cached === entry) cached = null;
      return null;
    });
  cached = entry;
  return entry.promise;
}

/** Сбросить запомненный адрес (смена кабинета в AuthProvider, тесты). */
export function resetShopSiteUrlCache(): void {
  cached = null;
}

/** Абсолютный адрес (https://…) — сервер уже подставил адрес сайта магазина. */
export const isAbsoluteHref = (href: string): boolean => /^https?:\/\//i.test(href);

/** Адрес страницы сайта: абсолютный — как есть; путь `/legal/…` — с адресом сайта; иначе null. */
export function siteHref(siteUrl: string | null, href: string): string | null {
  if (isAbsoluteHref(href)) return href;
  return siteUrl ? `${siteUrl}${href}` : null;
}

/** Адрес сайта; `needed = false` — все адреса уже абсолютные, запрос не нужен. */
export function useShopSiteUrl(needed = true): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!needed) return;
    let cancelled = false;
    void loadSiteUrl().then((next) => !cancelled && setUrl(next));
    return () => {
      cancelled = true;
    };
  }, [needed]);
  return url;
}
